import { CONFIG, PALETTES, setMode, setQuality } from './config/CONFIG.js';
import { AudioEngine } from './audio/AudioEngine.js';
import { Visuals } from './visuals/Visuals.js';
import { WeatherAPI } from './weather/WeatherAPI.js';
import { Timer, initTimerElements } from './timer/Timer.js';
import { UIManager, initUIElements } from './ui/UIManager.js';
import { Color } from 'three';

// 复用的临时 Color 对象，避免主循环每帧分配（GC 压力）
const _targetSky = new Color();
const _targetCloud = new Color();
const _lerpTmp = new Color();

// 质量档位到着色器分辨率系数的映射，提到循环外避免每帧重建
const QUALITY_MAP: Record<string, number> = { low: 0.5, medium: 0.75, high: 1.0 };
let _lastQuality = '';

// 音频强度节流：setIntensity 内部用 setTargetAtTime（3s 时间常数）平滑，
// 每帧调用是浪费。仅当强度变化超过阈值时才重新调度。
let _lastAppliedIntensity = -1;
const INTENSITY_EPSILON = 0.01;

// 渲染循环句柄。标签页隐藏时停止 rAF 以节省 GPU/电量，可见时恢复。
let _rafId: number | null = null;

function update() {
  const now = Date.now();
  const dateNow = new Date();
  const hour = dateNow.getHours() + dateNow.getMinutes() / 60;

  const targetSky = _targetSky, targetCloud = _targetCloud;
  let targetRain: number, targetSnow: number, targetLightning: number;
  let thunderAge = 999;

  if (CONFIG.mode === 'auto') {
    let idx = 0;
    for (let i = 0; i < PALETTES.length; i++) {
      if (hour >= PALETTES[i].hour) idx = i;
    }
    const nextIdx = (idx + 1) % PALETTES.length;
    const p1 = PALETTES[idx];
    const p2 = PALETTES[nextIdx];

    let dur = p2.hour - p1.hour;
    if (dur < 0) dur += 24;
    let elap = hour - p1.hour;
    if (elap < 0) elap += 24;
    const alpha = elap / dur;

    targetSky.set(p1.sky).lerp(_lerpTmp.set(p2.sky), alpha);
    targetCloud.set(p1.cloud).lerp(_lerpTmp.set(p2.cloud), alpha);
    targetRain = p1.rain + (p2.rain - p1.rain) * alpha;
    targetSnow = 0.0;
    targetLightning = 0.0;
  } else if (CONFIG.mode === 'heavy') {
    targetSky.set('#050505');
    targetCloud.set('#222222');
    targetRain = 1.0;
    targetSnow = 0.0;
    targetLightning = 0.0;
  } else if (CONFIG.mode === 'light') {
    targetSky.set('#334455');
    targetCloud.set('#aabbcc');
    targetRain = 0.2;
    targetSnow = 0.0;
    targetLightning = 0.0;
  } else if (CONFIG.mode === 'snow') {
    targetSky.set('#1a2535');
    targetCloud.set('#8090a0');
    targetRain = 0.0;
    targetSnow = 1.0;
    targetLightning = 0.0;
  } else {
    targetSky.set('#030308');
    targetCloud.set('#1a1a25');
    targetRain = 1.0;
    targetSnow = 0.0;

    if (now > CONFIG.thunderCooldown) {
      CONFIG.thunderCooldown = now + (3000 + Math.random() * 7000);
      CONFIG.lastThunderTime = now;
    }

    thunderAge = (now - CONFIG.lastThunderTime) / 1000;
    if (thunderAge < 0.2) {
      targetLightning = Math.max(0, 1.0 - thunderAge * 5);
    } else {
      targetLightning = 0.0;
    }
  }

  CONFIG.params.skyColor.lerp(targetSky, 0.008);
  CONFIG.params.cloudColor.lerp(targetCloud, 0.008);
  CONFIG.params.rainIntensity += (targetRain - CONFIG.params.rainIntensity) * 0.008;
  CONFIG.params.snowIntensity += (targetSnow - CONFIG.params.snowIntensity) * 0.008;
  CONFIG.params.lightningFlash = targetLightning;

  if (Visuals.material) {
    Visuals.material.uniforms.u_time.value = (now - CONFIG.startTime) / 1000;
    Visuals.material.uniforms.u_skyColor.value.copy(CONFIG.params.skyColor);
    Visuals.material.uniforms.u_cloudColor.value.copy(CONFIG.params.cloudColor);
    Visuals.material.uniforms.u_rainIntensity.value = CONFIG.params.rainIntensity;
    Visuals.material.uniforms.u_snowIntensity.value = CONFIG.params.snowIntensity;
    Visuals.material.uniforms.u_lightningFlash.value = CONFIG.params.lightningFlash;

    // u_quality 只在档位切换时更新
    if (CONFIG.quality !== _lastQuality) {
      Visuals.material.uniforms.u_quality.value = QUALITY_MAP[CONFIG.quality] || 1.0;
      _lastQuality = CONFIG.quality;
    }
  }

  if (CONFIG.isPlaying) {
    // snow 模式固定 0.3，其余跟随雨量；仅在目标值变化超过阈值时才重新调度
    const targetIntensity = CONFIG.mode === 'snow' ? 0.3 : CONFIG.params.rainIntensity;
    if (Math.abs(targetIntensity - _lastAppliedIntensity) > INTENSITY_EPSILON) {
      AudioEngine.setIntensity(targetIntensity);
      _lastAppliedIntensity = targetIntensity;
    }
    if (CONFIG.mode === 'thunderstorm' && thunderAge < 0.1) {
      AudioEngine.playThunder();
    }
  } else {
    // 暂停时重置，下次播放能立即重新调度
    _lastAppliedIntensity = -1;
  }

  if (Visuals.renderer && Visuals.scene && Visuals.camera) {
    Visuals.renderer.render(Visuals.scene, Visuals.camera);
  }

  _rafId = requestAnimationFrame(update);
}

// 标签页可见性控制：隐藏时暂停渲染循环，恢复时重启（避免后台空转）
function startLoop() {
  if (_rafId === null) {
    _rafId = requestAnimationFrame(update);
  }
}

function stopLoop() {
  if (_rafId !== null) {
    cancelAnimationFrame(_rafId);
    _rafId = null;
  }
}

function setupUI() {
  const playBtn = document.getElementById('play-btn');
  const volContainer = document.getElementById('vol-wrap');
  const volSlider = document.getElementById('volume-slider') as HTMLInputElement;
  const uiLayer = document.getElementById('ui-layer');

  if (uiLayer) {
    initUIElements(uiLayer);
  }

  (window as any).setMode = setMode;

  if (playBtn) {
    playBtn.addEventListener('click', () => {
      if (!CONFIG.isPlaying) {
        AudioEngine.init();
        AudioEngine.setVolume(CONFIG.volume);
        playBtn.classList.add('playing');
        if (volContainer) {
          volContainer.style.opacity = '1';
          volContainer.style.transform = 'translateY(0)';
        }
        CONFIG.isPlaying = true;
      } else {
        AudioEngine.setVolume(0);
        setTimeout(() => AudioEngine.resume(), 200);
        playBtn.classList.remove('playing');
        CONFIG.isPlaying = false;
      }
    });
  }

  if (volSlider) {
    volSlider.addEventListener('input', (e) => {
      CONFIG.volume = parseFloat((e.target as HTMLInputElement).value);
      if (CONFIG.isPlaying) {
        AudioEngine.setVolume(CONFIG.volume);
      }
    });
  }

  const soundSliders = {
    rumble: document.getElementById('sound-rumble') as HTMLInputElement,
    rain: document.getElementById('sound-rain') as HTMLInputElement,
    wind: document.getElementById('sound-wind') as HTMLInputElement,
    hiss: document.getElementById('sound-hiss') as HTMLInputElement,
    birds: document.getElementById('sound-birds') as HTMLInputElement,
    fire: document.getElementById('sound-fire') as HTMLInputElement,
    stream: document.getElementById('sound-stream') as HTMLInputElement,
    windchime: document.getElementById('sound-windchime') as HTMLInputElement,
  };

  Object.entries(soundSliders).forEach(([key, slider]) => {
    if (slider) {
      slider.addEventListener('input', (e) => {
        const value = parseFloat((e.target as HTMLInputElement).value);
        (CONFIG.soundLevels as any)[key] = value;
        if (CONFIG.isPlaying) {
          AudioEngine.setSoundLevel(key as any, value);
        }
      });
    }
  });

  const weatherSyncToggle = document.getElementById('weather-sync-toggle') as HTMLInputElement;
  const weatherInfo = document.getElementById('weather-info');

  if (weatherSyncToggle) {
    weatherSyncToggle.addEventListener('change', async (e) => {
      CONFIG.weatherSyncEnabled = (e.target as HTMLInputElement).checked;

      if (CONFIG.weatherSyncEnabled) {
        if (weatherInfo) weatherInfo.textContent = 'Fetching location...';
        await WeatherAPI.init();
        await WeatherAPI.applyWeatherMode();
      } else {
        if (weatherInfo) weatherInfo.textContent = 'Click toggle to sync';
      }
    });
  }

  const qualityBtns = document.querySelectorAll('.quality-btn');
  qualityBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      qualityBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      const q = (btn as HTMLElement).dataset.quality as any;
      setQuality(q);
      Visuals.applyQuality(q);
    });
  });

  const timerDisplay = document.getElementById('timer-display');
  const timerStartBtn = document.getElementById('timer-start');
  const timerResetBtn = document.getElementById('timer-reset');
  const timerPresetBtns = document.querySelectorAll('.timer-preset');

  if (timerDisplay && timerStartBtn && playBtn) {
    initTimerElements(timerDisplay, timerStartBtn, playBtn);

    timerStartBtn.addEventListener('click', () => {
      Timer.start();
    });

    if (timerResetBtn) {
      timerResetBtn.addEventListener('click', () => {
        Timer.reset();
      });
    }

    timerPresetBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        timerPresetBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const minutes = parseInt((btn as HTMLElement).dataset.minutes || '25');
        Timer.setDuration(minutes);
      });
    });

    Timer.updateDisplay();
  }

  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }

  setInterval(async () => {
    if (CONFIG.weatherSyncEnabled) {
      await WeatherAPI.applyWeatherMode();
    }
  }, 10 * 60 * 1000);

  UIManager.setupAutoHide();
}

function init() {
  Visuals.init();
  Visuals.applyQuality(CONFIG.quality);
  setupUI();

  // 标签页隐藏时暂停渲染循环，恢复时重启，避免后台空转浪费 GPU/电量
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopLoop();
    } else {
      startLoop();
    }
  });

  startLoop();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
